import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Button from '../../components/Button';
import Wordmark from '../../components/Wordmark';
import { WizardStatus } from '../../components/WizardShell';
import { RENDER_DISCLAIMER } from '../../data/style-library';
import {
  api,
  type Concept,
  type DnaResponse,
  type PlanResponse,
  type Project,
} from '../../lib/api';

/**
 * The Design Brief — the takeaway artifact (CLAUDE.md §9.3).
 *
 * Printed by the browser rather than rendered server-side. A Worker has no
 * canvas and cannot run a headless browser, so a real PDF would mean adding
 * Cloudflare Browser Rendering as a paid dependency. Print-to-PDF gets the same
 * file, on every platform, for nothing — and it keeps selectable text and live
 * links, which a rasterised PDF would lose.
 *
 * The print stylesheet lives here rather than in tokens.css because it only
 * applies to this page and reads better next to the markup it governs.
 */
const PRINT_CSS = `
@media print {
  @page { margin: 16mm; }
  html, body { background: #fff !important; }
  .no-print { display: none !important; }
  .brief-page { break-after: page; }
  .brief-block { break-inside: avoid; }
  a[href^="http"]::after { content: " (" attr(href) ")"; font-size: 0.75em; color: #55595b; }
  img { max-height: 9cm; object-fit: contain; }
}
`;

const n = (v: number) => v.toLocaleString('en-US');
const money = (v: number) =>
  v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : `$${Math.round(v / 1000)}k`;

export default function Brief() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [dna, setDna] = useState<DnaResponse | null>(null);
  const [plan, setPlan] = useState<PlanResponse | null>(null);
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    // Each piece is optional: a brief for a project that has answers but no
    // concepts yet is still worth printing.
    Promise.allSettled([
      api.getProject(id),
      api.getDna(id),
      api.getPlan(id),
      api.getConcepts(id),
    ]).then(([p, d, pl, c]) => {
      if (cancelled) return;
      if (p.status === 'fulfilled') setProject(p.value);
      else setError('We could not load this project.');
      if (d.status === 'fulfilled') setDna(d.value);
      if (pl.status === 'fulfilled') setPlan(pl.value);
      if (c.status === 'fulfilled') setConcepts(c.value.concepts);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) return <WizardStatus message={error} />;
  if (!project) return <WizardStatus message="Putting the brief together…" />;

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="brief-block mt-10 border-t border-grey-200 pt-8">
      <h2 className="t-h2 text-ink">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );

  const Row = ({ label, value }: { label: string; value: string }) => (
    <div className="flex justify-between gap-6 border-b border-grey-200 py-2">
      <dt className="t-meta text-grey-600">{label}</dt>
      <dd className="t-meta t-numeral text-right text-ink">{value}</dd>
    </div>
  );

  return (
    <div className="min-h-dvh bg-white">
      <style>{PRINT_CSS}</style>

      <div className="no-print border-b border-grey-200 bg-grey-050 px-gutter py-4">
        <div className="mx-auto flex w-full max-w-wrap flex-wrap items-center justify-between gap-4">
          <p className="t-meta text-grey-600">
            Print this, or save it as a PDF, and take it to your design professional.
          </p>
          <Button onClick={() => window.print()}>Print or save as PDF</Button>
        </div>
      </div>

      <main className="mx-auto w-full max-w-3xl px-gutter py-12">
        {/* Cover */}
        <header className="brief-block">
          <div className="text-xl">
            <Wordmark />
          </div>
          <p className="t-eyebrow mt-10 text-green-600">Design brief</p>
          <h1 className="t-display mt-3 text-ink">
            {project.name ?? plan?.verticalLabel ?? 'Your building'}
          </h1>
          <p className="t-body-lg measure mt-5 text-grey-600">
            Prepared for {project.name ? '' : 'you '}on{' '}
            {new Date().toLocaleDateString('en-US', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
            . Everything here is a starting point, and every figure is a range.
          </p>
        </header>

        {plan && (
          <Section title="The building">
            <dl>
              <Row label="Type" value={plan.verticalLabel} />
              <Row label="Size" value={`${n(plan.sizeSf)} sf`} />
              <Row label="Stories" value={String(plan.stories)} />
              {plan.systemLabel && <Row label="Construction" value={plan.systemLabel} />}
            </dl>
          </Section>
        )}

        {dna?.brief && (
          <Section title="Design direction">
            <p className="t-body-lg measure text-ink">{dna.brief}</p>
            {dna.briefIsPartial && (
              <p className="t-meta measure mt-3 text-grey-600">
                The picks pulled in more than one direction; this describes the strongest
                single reading of them.
              </p>
            )}
          </Section>
        )}

        {dna && dna.picks.length > 0 && (
          <Section title="The buildings that were chosen">
            <ul className="flex flex-wrap gap-2">
              {dna.picks.map((p) => (
                <li key={p.id}>
                  <img
                    src={p.url}
                    alt={p.caption ?? ''}
                    className="h-24 w-32 rounded-sm border border-grey-200 object-cover"
                  />
                </li>
              ))}
            </ul>
          </Section>
        )}

        {concepts.length > 0 && (
          <Section title="Concepts">
            <ul className="flex flex-col gap-6">
              {concepts.slice(0, 3).map((c, i) => (
                <li key={c.id} className="brief-block">
                  <img src={c.url} alt={`Concept ${i + 1}`} className="w-full rounded-sm border border-grey-200" />
                  <p className="t-meta mt-2 text-grey-600">Concept {i + 1}</p>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {plan?.site && (
          <Section title="Site and parking">
            <dl>
              <Row label="Building footprint" value={`${n(plan.site.footprint.low)} sf`} />
              <Row
                label="Parking spaces"
                value={`${n(plan.site.stalls.low)}–${n(plan.site.stalls.high)}`}
              />
              <Row
                label="Land needed"
                value={`${plan.site.acres.low}–${plan.site.acres.high} acres`}
              />
            </dl>
            <p className="t-meta measure mt-3 text-grey-600">
              Parking counts come from your local zoning ordinance. Confirm with your
              jurisdiction.
            </p>
          </Section>
        )}

        {plan?.electrical && (
          <Section title="Electrical and lighting">
            <dl>
              <Row
                label="Connected load"
                value={`${n(plan.electrical.connectedLoadKw.low)}–${n(plan.electrical.connectedLoadKw.high)} kW`}
              />
              <Row
                label="Demand load"
                value={`${n(plan.electrical.demandLoadKw.low)}–${n(plan.electrical.demandLoadKw.high)} kW`}
              />
              <Row label="Service" value={plan.electrical.serviceDescription} />
              <Row
                label="Interior fixtures"
                value={`${n(plan.electrical.interiorFixtures.low)}–${n(plan.electrical.interiorFixtures.high)}`}
              />
              <Row
                label="Exterior fixtures"
                value={`${n(plan.electrical.exteriorFixtures.low)}–${n(plan.electrical.exteriorFixtures.high)}`}
              />
            </dl>
            <p className="t-meta measure mt-3 text-grey-600">
              Planning figures from the building type and size, not a load calculation.
              Utility capacity at the road is a separate question and often the long pole.
            </p>
          </Section>
        )}

        {plan?.budget && (
          <Section title="Budget, order of magnitude">
            <dl>
              <Row
                label="Cost per square foot"
                value={`$${plan.budget.perSf.low}–$${plan.budget.perSf.high}`}
              />
              <Row
                label="Hard cost"
                value={`${money(plan.budget.hard.low)}–${money(plan.budget.hard.high)}`}
              />
              <Row
                label="Soft cost"
                value={`${money(plan.budget.soft.low)}–${money(plan.budget.soft.high)}`}
              />
              <Row
                label="Total"
                value={`${money(plan.budget.total.low)}–${money(plan.budget.total.high)}`}
              />
            </dl>
            <p className="t-meta measure mt-3 text-grey-600">
              At concept stage, before there are drawings, treat this as plus or minus
              thirty per cent. Land, offsite utilities, furniture and equipment are not in
              it.
            </p>
          </Section>
        )}

        {plan?.occupancy && (
          <Section title="Code, at a glance">
            <dl>
              <Row label="Likely occupancy group" value={plan.occupancy.occupancyGroup} />
              <Row
                label="Occupant load"
                value={`${n(plan.occupancy.occupantLoad.low)}–${n(plan.occupancy.occupantLoad.high)}`}
              />
              <Row label="Exits required" value={String(plan.occupancy.minimumExits)} />
              <Row
                label="Water closets"
                value={`${plan.occupancy.waterClosets.low}–${plan.occupancy.waterClosets.high}`}
              />
            </dl>
            <p className="t-meta measure mt-3 text-grey-600">
              Preliminary rules of thumb. The adopted code edition and any local amendments
              are set by your authority having jurisdiction — verify with them.
            </p>
          </Section>
        )}

        <section className="brief-block mt-12 border-t-[3px] border-t-green-600 pt-8">
          <h2 className="t-h2 text-ink">Etrenzik Design</h2>
          <p className="t-body measure mt-3 text-grey-600">
            Questions about anything in this brief, or want the team to take another
            look at it? Get in touch.
          </p>
          <p className="t-body mt-3 text-ink">
            <a href="mailto:info@etrenzik.com" className="font-semibold text-green-700 underline decoration-1 underline-offset-4">
              info@etrenzik.com
            </a>
          </p>
        </section>

        <footer className="brief-block mt-10 border-t border-grey-200 pt-6">
          <p className="t-meta measure text-grey-600">{RENDER_DISCLAIMER}</p>
        </footer>
      </main>
    </div>
  );
}
